/**
 * **성능 예산** — 게임이 한 프레임에 쓸 수 있는 몫. 배포 전에 `npm run budget`(tools/budget.sh)이 게임을 띄워
 * 방마다 순간이동하며 실제 씬을 재고, 넘으면 배포를 막는다.
 *
 * 왜: 조명 수·draw call 이 조금씩 늘어도 한동안은 멀쩡하다가 어느 날 프레임이 반토막 난다. 2026-09-26 맥북 M2 Pro
 * 에서 게임이 14fps 였던 원인을 사람이 손으로 찾았다 — 이 숫자들은 그때 잰 것이고, 다시 손으로 찾지 않으려고 여기 박는다.
 */

/**
 * 점광·스포트 조명 합의 상한. three 표준 재질은 픽셀마다 이 조명을 **전부**(세기 0 인 빈 슬롯까지) 돈다.
 * 맥북 M2 Pro 게임 방 안: 합 16 이하 77fps, 17 이면 50fps — 선형이 아니라 절벽이다.
 * 방향광·반구광은 따로 친다(개수가 적고 싸다).
 */
export const LIGHT_BUDGET = 16;

/**
 * 조명 슬롯 배분. 합이 LIGHT_BUDGET 을 넘으면 안 된다. **개수는 게임 내내 고정** — 조명이 씬에 들어오거나 나가면
 * three 가 모든 재질을 다시 컴파일해 화면이 멎는다. 그래서 모든 동적 조명은 슬롯에 값만 갈아 끼운다.
 */
export const LIGHT_SLOTS = {
  /** 방 조명 풀 스포트(world.ts). 방 하나의 스포트는 최대 6개라 내 방은 다 켜진다 */
  roomSpot: 6,
  /** 방 조명 풀 포인트(world.ts) — 방마다 필 1개 */
  roomPoint: 3,
  /** 가로등·현관등 중 가까운 것(lightPool.ts). 단서 사진 플래시도 여기서 한 칸 빌린다(photo.ts) */
  decor: 3,
  /** 남의 손전등 중 가까운 사람(remote.ts) */
  remoteLamp: 2,
  /** 내 손전등 스포트(player.ts) */
  flashlight: 1,
  /** 시신 곁 등(props.ts) */
  corpse: 1,
} as const;

/**
 * 한 프레임의 draw call·삼각형 상한, 그림자 메시 상한, 씬 텍스처 총량 상한.
 * 2026-09-26 게임(q=high)에서 방 53개·거리·조감도를 돌며 잰 최댓값(괄호)에 10~25% 여유를 둔 값이다.
 * 넘으면 병합·컬링(world.ts batchPieces·cullPieces)부터 볼 것. 올려야 한다면 맥북에서 fps 를 재고 올린다
 * (`npx tsx tools/gpu-fps.ts <주소>`).
 */
export const FRAME_BUDGET = {
  /**
   * 카메라 시야 안의 draw call(본 패스). **이게 맥북에서 가장 먼저 막힌다** — 조합 홀이 본 패스 1,256 에 GTAO 가
   * 씬을 한 번 더 그려 2,546 이었을 때 53fps(해상도를 반으로 줄여도 그대로), GTAO 가 본 패스 깊이를 쓰게 한 뒤
   * 63fps. 1,400 을 넘으면 60 아래로 내려간다고 보면 된다. (1,256 · 조합 홀)
   */
  drawCalls: 1400,
  /** (1.02M · 조합 홀) */
  triangles: 1_300_000,
  /** 그림자를 지는 메시 수 — 그림자 맵을 갱신하는 프레임(여럿이 있거나 움직일 때 초당 20번)마다 한 번 더 그린다. (1,446) */
  shadowCasters: 1700,
  /** (27.5MP) */
  textureMegapixels: 35,
};

/**
 * 조감도(M)는 마을 전체를 한 번에 그린다 — 따로 잰다. **지금 맥북 26fps**(draw call 약 5천·삼각형 590만, 해상도를
 * 줄여도 그대로라 그리는 양이 병목). 먼 방은 큰 가구만 남기는 LOD 가 다음 일이다(ROADMAP). 그때까지는 더 나빠지지만
 * 않게 막는다. (4,837 · 2.77M · 4,278)
 */
export const OVERVIEW_BUDGET = {
  drawCalls: 5400,
  triangles: 3_100_000,
  shadowCasters: 4800,
};
